import React, { useEffect, useState } from "react";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import "./DeliveryTable.css";
import { DownloadSingleReportConfig, LoadoutDetailsType, loadoutStatusConfig } from "../../types/types";
import { useNavigate } from "react-router-dom";
import { GridRenderCellParams, GridTreeNodeWithRender } from "@mui/x-data-grid";
import { fetchLoadoutConfigs } from "../../components/loadouts/pre-seller/PresellerLoadoutService";
import { markShortageQtyForLoadouts } from "./DeliveryConfirmationService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import CustomQuantityDisplay from "../../components/dms_common/customQuantityDisplay";

const LoadoutDeliveryTable = (props: { data: LoadoutDetailsType[], refreshData: () => void, isPartialDeliveryAllowed:boolean, enableSalesmanAssignment:boolean}) => {
    const [data, setData] = useState<LoadoutDetailsType[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [downloadSingleReportConfig, setDownloadSingleReportConfig] = useState<DownloadSingleReportConfig | null>();
    const [statusMapping, setStatusMapping] = useState<loadoutStatusConfig[]>([]);
    const navigate = useNavigate(); // For navigation

    const columnsMetaData = {
        columns: [
            {
                headerName: "Loadout No.",
                mappedValue: "loadNumber",
                cellRenderElement: (params: GridRenderCellParams<LoadoutDetailsType, any, any, GridTreeNodeWithRender>) => (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", width: "9rem", whiteSpace: "nowrap" }}>
                            {params?.row?.loadNumber}
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                            {params.row.loadOutDetails.length} {params.row.loadOutDetails.length > 1 ? "Invoices" : "Invoice"}
                        </div>
                    </div>
                ),
                width: 13
            },
            {
                headerName: "Sales Man Name & ID",
                headerParts: { top: "Sales Man", bottom: "Name & ID" },
                cellRenderElement: (params: GridRenderCellParams) => (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", width: "9rem", whiteSpace: "nowrap" }}>
                            {params?.row?.salesmanName}
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                            {params?.row?.salesmanId}
                        </div>
                    </div>
                ),
                mappedValue: "salesmanName",
                width: 13,
            },
            {
                headerName: "Delivery Date",
                width: 14,
                mappedValue: "loadOutDate"
            },
            {
                headerName: "Loadout Qty",
                mappedValue: "totalCaseQty,totalPieceQty,totalOtherQty",
                cellRenderElement: (params: GridRenderCellParams) => (
                    <CustomQuantityDisplay caseQty={params?.row?.totalCaseQty} pieceQty={params?.row?.totalPieceQty} otherQty={params?.row?.totalOtherQty}/>
                ),
                width: 13,
            },
            {
                headerName: "Sold Qty",
                mappedValue: "soldCaseQty,soldPieceQty,soldOtherQty",
                width: 13,
                cellRenderElement: (params: GridRenderCellParams) => (
                    <CustomQuantityDisplay caseQty={params?.row?.soldCaseQty} pieceQty={params?.row?.soldPieceQty} otherQty={params?.row?.soldOtherQty}/>
                )
            },

            {
                headerName: "Return Qty",
                mappedValue: "totalCaseLeftQty,totalPieceLeftQty,totalOtherLeftQty",
                width: 13,
                cellRenderElement: (params: GridRenderCellParams) => (
                    <CustomQuantityDisplay caseQty={params?.row?.totalCaseLeftQty} pieceQty={params?.row?.totalPieceLeftQty} otherQty={params?.row?.totalOtherLeftQty}/>
                )
            },

            // {
            //     headerName: "Actual Return Qty",
            //     headerParts: { top: "Actual", bottom: "Return Qty" },
            //     mappedValue: "actualCaseReturnedQty,actualPieceReturnedQty,actualOtherReturnedQty",
            //     isEditableMappedValue:"isActualQtyEditable",
            //     width: 10,
            //     hasEditPenSvg:true,
            //     headerAlign: "left",
            //     align: "left",
            //     type: "stocks",
            //     qtyDisplay: true
            // },
            // {
            //     headerName: "Shortage Qty",
            //     headerParts: { top: "Shortage", bottom: "Qty" },
            //     mappedValue: "caseShortage,pieceShortage,otherShortage",
            //     cellRenderElement: (params: GridRenderCellParams) => (
            //         <CustomQuantityDisplay caseQty={params?.row?.caseShortage} pieceQty={params?.row?.pieceShortage} otherQty={params?.row?.otherShortage}  colorScheme={params?.row?.shortageUpdated ? "red" : "lightgrey"}/>
            //     ),
            //     width: 10,
            // },
            {
                headerName: "Status",
                mappedValue: "status",
                width: 10
            },
        ],
        Actions: ["view"],
    };

    if(!props.enableSalesmanAssignment){
        columnsMetaData.columns = columnsMetaData.columns.filter(column=>column.mappedValue!="salesmanName");
    }

    if(!props.isPartialDeliveryAllowed){
        columnsMetaData.columns = columnsMetaData.columns.filter((column) => column.headerName !== "Actual Return Qty" && column.headerName != "Shortage Qty");
    }

    const handleRedirect = async (redirectUrl: string, rowData: LoadoutDetailsType) => {
        navigate("loadoutDetails", {
            state:{
                loadNumber:rowData.loadNumber,
                loadOutDate:rowData.loadOutDate,
                isPartialDeliveryAllowed: props.isPartialDeliveryAllowed
            }
        });
    };

    const fetchData = async () => {
        try {
            const response = props.data;
            const formattedData = response.map((row) => ({
                ...row,
                id: row.loadNumber, // Add unique `id` field
            }));
            const loadoutStages = await fetchLoadoutConfigs("loadoutStages");
            setStatusMapping(loadoutStages);
            setData(formattedData);
        } catch (error) {
            console.error("Error fetching delivery data:", error);
        }
    };


    useEffect(() => {
        fetchData();
    }, [props.data]);

    const markShortageQty = async()=>{
        setIsSubmitting(true);
        try{
            const editedLoadouts = data.filter(loadout=>loadout.isActualQtyEditable && (loadout.actualCaseReturnedQty+loadout.actualOtherReturnedQty+loadout.actualPieceReturnedQty>0))
            if(editedLoadouts.length == 0){
                openPopup("Error", "Actual return qty not marked");
                setIsSubmitting(false);
                return;
            }
            try{
                validateAndEnrichShortageQty(editedLoadouts);
            }catch(e){
                openPopup("Error", "Actual return qty can not be greater than return qty");
                setIsSubmitting(false);
                return;
            }
            await markShortageQtyForLoadouts(editedLoadouts);
            props.refreshData();
        }catch(e){
            openPopup("Error","Shortage not marked sucessfully, please try again");
        }
        setIsSubmitting(false);
    }
    const validateAndEnrichShortageQty=(editedLoadouts:LoadoutDetailsType[])=>{
        editedLoadouts.forEach((loadout)=>{
            const caseShortage = loadout.totalCaseLeftQty - loadout.actualCaseReturnedQty;
            const pieceShortage = loadout.totalPieceLeftQty - loadout.actualPieceReturnedQty;
            const otherShortage = loadout.totalOtherLeftQty - loadout.actualOtherReturnedQty;
            if(caseShortage<0 || pieceShortage<0 || otherShortage<0){
                throw Error("Actual return qty exceeding return qty for loadout:"+loadout.loadNumber);
            }
            loadout.caseShortage = caseShortage;
            loadout.pieceShortage = pieceShortage;
            loadout.otherShortage = otherShortage;
        });
    }

    const submitFooter = () => {
        if(!props.isPartialDeliveryAllowed){
            return (<></>);
        }
        return (
            <div className="loadout-delivery-submit-button-container">
                <button
                    className={`loadout-delivery-submit ${isSubmitting ? "disabled" : ""}`}
                    onClick={markShortageQty}
                >
                    <span className="loadout-delivery-submit-text">
                        Update Shortage
                    </span>
                </button>
            </div>
        );
    }

    return (
        <CustomDataGrid
            rows={Array.isArray(data) ? data : []}
            columnsMetaData={columnsMetaData}
            tableWidth={0.74}
            redirectUrl="loadoutDetails"
            redirectUrlFunction={handleRedirect}
            orderStatusConfig={statusMapping}
            // footerElement={submitFooter}
            downloadSingleReportConfig={downloadSingleReportConfig}
        />
    );
};


export default LoadoutDeliveryTable;